// components/EditBroadbandInfoModal.jsx
import React, { useState, useEffect } from "react";
import { X, Loader, Wifi } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const EditBroadbandInfoModal = ({ broadbandInfo, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    broadbandSupplier: "",
    orderType: "",
    type: "",
    expectedSpeeds: "",
    wan: "",
    localIp: "",
    dslUsername: "",
    dslPassword: "",
    lineNumber: "",
    requestedDate: "",
    acceptedDate: "",
    broadbandInstalled: false,
    notes: "",
  });

  useEffect(() => {
    if (broadbandInfo) {
      setFormData({
        broadbandSupplier: broadbandInfo.broadbandSupplier || "",
        orderType: broadbandInfo.orderType || "",
        type: broadbandInfo.type || "",
        expectedSpeeds: broadbandInfo.expectedSpeeds || "",
        wan: broadbandInfo.wan || "",
        localIp: broadbandInfo.localIp || "",
        dslUsername: broadbandInfo.dslUsername || "",
        dslPassword: broadbandInfo.dslPassword || "",
        lineNumber: broadbandInfo.lineNumber || "",
        requestedDate: broadbandInfo.requestedDate
          ? broadbandInfo.requestedDate.split("T")[0]
          : "",
        acceptedDate: broadbandInfo.acceptedDate
          ? broadbandInfo.acceptedDate.split("T")[0]
          : "",
        broadbandInstalled: broadbandInfo.broadbandInstalled || false,
        notes: broadbandInfo.notes || "",
      });
    }
  }, [broadbandInfo]);

  const sanitisePayload = (data) => {
    const DATE_FIELDS = ["requestedDate", "acceptedDate"];
    const sanitised = { ...data };
    for (const field of DATE_FIELDS) {
      if (!sanitised[field] || sanitised[field].trim() === "") {
        sanitised[field] = null;
      }
    }
    return sanitised;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await apiService.updateBroadbandInfo(
        broadbandInfo.id,
        sanitisePayload(formData),
      );
      onSuccess();
    } catch (error) {
      console.error("Error updating broadband info:", error);
      alert(
        error.response?.data?.message ||
          "Failed to update broadband information",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode
            ? "bg-[#13161B] border border-gray-700"
            : "bg-white border border-gray-300"
        }`}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b flex items-center justify-between ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isDarkMode
                  ? "bg-blue-600"
                  : "bg-blue-100 border border-blue-200"
              }`}
            >
              <Wifi
                size={20}
                className={isDarkMode ? "text-white" : "text-blue-600"}
              />
            </div>
            <div>
              <h2
                className={`text-lg font-semibold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Edit Broadband Information
              </h2>
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {broadbandInfo.biRef}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto p-6">
          <form
            onSubmit={handleSubmit}
            id="broadbandForm"
            className="space-y-6"
          >
            {/* Basic Information */}
            <div className="space-y-6">
              <div
                className={`flex items-center gap-3 pb-4 border-b ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <div
                  className={`w-1 h-5 rounded-full ${
                    isDarkMode ? "bg-blue-500" : "bg-blue-600"
                  }`}
                ></div>
                <h3
                  className={`text-base font-semibold ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Basic Information
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Broadband Supplier
                  </label>
                  <select
                    name="broadbandSupplier"
                    value={formData.broadbandSupplier}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">--None--</option>
                    <option value="Gamma">Gamma</option>
                    <option value="Geocom">Geocom</option>
                    <option value="Openreach">Openreach</option>
                    <option value="Glide">Glide</option>
                    <option value="ITS">ITS</option>
                    <option value="Eclipse">Eclipse</option>
                    <option value="Elevate">Elevate</option>
                    <option value="Toob">Toob</option>
                    <option value="Giacom">Giacom</option>
                    <option value="G.Network">G.Network</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Order Type
                  </label>
                  <select
                    name="orderType"
                    value={formData.orderType}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">--None--</option>
                    <option value="New Provide">New Provide</option>
                    <option value="Upgrade">Upgrade</option>
                    <option value="Cease">Cease</option>
                    <option value="Transfer">Transfer</option>
                    <option value="Migrate">Migrate</option>
                  </select>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Type
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">--None--</option>
                    <option value="ADSL">ADSL</option>
                    <option value="SOGEA">SOGEA</option>
                    <option value="FTTP">FTTP</option>
                    <option value="FIBRE LIGHT">FIBRE LIGHT</option>
                    <option value="FIBRE BRIGHT">FIBRE BRIGHT</option>
                    <option value="ETHERNET">ETHERNET</option>
                    <option value="CITY FIBRE">CITY FIBRE</option>
                    <option value="STARLINK">STARLINK</option>
                    <option value="Mobile 4G">Mobile 4G</option>
                    <option value="Mobile 5G">Mobile 5G</option>
                    <option value="Voice Router">Voice Router</option>
                    <option value="Data Router">Data Router</option>
                    <option value="Combined Router">Combined Router</option>
                    <option value="Customer Owned">Customer Owned</option>
                  </select>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Expected Speeds
                  </label>
                  <input
                    type="text"
                    name="expectedSpeeds"
                    value={formData.expectedSpeeds}
                    onChange={handleChange}
                    placeholder="e.g., 100Mbps"
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Line Number
                  </label>
                  <input
                    type="text"
                    name="lineNumber"
                    value={formData.lineNumber}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                  />
                </div>
              </div>
            </div>

            {/* Network Information */}
            <div className="space-y-6">
              <div
                className={`flex items-center gap-3 pb-4 border-b ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <div
                  className={`w-1 h-5 rounded-full ${
                    isDarkMode ? "bg-blue-500" : "bg-blue-600"
                  }`}
                ></div>
                <h3
                  className={`text-base font-semibold ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Network Information
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    WAN
                  </label>
                  <input
                    type="text"
                    name="wan"
                    value={formData.wan}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Local IP
                  </label>
                  <input
                    type="text"
                    name="localIp"
                    value={formData.localIp}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    DSL Username
                  </label>
                  <input
                    type="text"
                    name="dslUsername"
                    value={formData.dslUsername}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    DSL Password
                  </label>
                  <input
                    type="password"
                    name="dslPassword"
                    value={formData.dslPassword}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                  />
                </div>
              </div>
            </div>

            {/* Dates & Status */}
            <div className="space-y-6">
              <div
                className={`flex items-center gap-3 pb-4 border-b ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <div
                  className={`w-1 h-5 rounded-full ${
                    isDarkMode ? "bg-blue-500" : "bg-blue-600"
                  }`}
                ></div>
                <h3
                  className={`text-base font-semibold ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Dates & Status
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Requested Date
                  </label>
                  <input
                    type="date"
                    name="requestedDate"
                    value={formData.requestedDate}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Accepted Date
                  </label>
                  <input
                    type="date"
                    name="acceptedDate"
                    value={formData.acceptedDate}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div className="col-span-2">
                  <label
                    className={`flex items-center gap-2 text-sm cursor-pointer ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="broadbandInstalled"
                      checked={formData.broadbandInstalled}
                      onChange={handleChange}
                      className={`w-4 h-4 rounded border focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "border-gray-700 bg-[#0C0E12] text-blue-600"
                          : "border-gray-300 bg-white text-blue-600"
                      }`}
                    />
                    Broadband Installed
                  </label>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-6">
              <div
                className={`flex items-center gap-3 pb-4 border-b ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <div
                  className={`w-1 h-5 rounded-full ${
                    isDarkMode ? "bg-blue-500" : "bg-blue-600"
                  }`}
                ></div>
                <h3
                  className={`text-base font-semibold ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Notes
                </h3>
              </div>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={4}
                className={`w-full px-3 py-2 border rounded resize-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                }`}
                placeholder="Additional notes..."
              />
            </div>
          </form>
        </div>

        {/* Footer */}
        <div
          className={`px-6 py-4 border-t flex justify-end gap-3 ${
            isDarkMode
              ? "border-gray-700 bg-[#0C0E12]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2 text-sm font-medium border rounded transition-colors ${
              isDarkMode
                ? "text-gray-300 bg-[#13161B] border-gray-700 hover:bg-gray-700"
                : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
            }`}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="broadbandForm"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading && <Loader size={16} className="animate-spin" />}
            {loading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditBroadbandInfoModal;
